/**
 * PublishChannelModal Component
 * Modal for selecting a publish channel (WhatsApp, Instagram, Site)
 * and connecting before publishing the agent.
 *
 * WhatsApp connection flow mirrors src/screens/WhatsappAgents.js:
 *   1. ConnectWhatsappAccount → creates WAHA session
 *   2. Poll CheckAccountStatus every 3s until WORKING / SCAN_QR_CODE
 *   3. Show <WhatsappConnect> for QR / code auth
 *   4. On success → publishAgent
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, useContext, lazy, Suspense } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import NumberFormat from 'react-number-format';
import {
  IconRocket,
  IconBrandWhatsapp,
  IconBrandInstagram,
  IconBrandMeta,
  IconWorld,
  IconX,
  IconCheck,
  IconAlertTriangle,
  IconPhone,
  IconTrash,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlugConnected,
  IconInfoCircle,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import {
  GetAccounts,
  GetWhatsappFeatureUsage,
  ConnectWhatsappAccount,
  CheckAccountStatus,
  DeleteAccount,
  PauseOrActiveAccount,
} from '../../../services/WaBottyServices';
import { api } from '../../../api/Middleware';
import { WABOTTY_URL } from '../../../api/Urls';
import { formatPhoneForAPI } from '../../../helpers/Helpers';
import AgentsFlowService from '../services/AgentsFlowService';
import usePublishValidation from '../hooks/usePublishValidation';
import NotificationsContext from '../../../contexts/NotificationsContext';
import UserContext from '../../../contexts/UserContext';
import { isCloudApiEnabledForAccount } from '../constants/featureFlags';
import './PublishChannelModal.css';

const WhatsappConnect = lazy(() => import('../../../jsx/components/WhatsAppChat/WhatsappConnect'));
const CloudApiConnectModal = lazy(() => import('../../WhatsAppCloudApi/components/CloudApiConnectModal'));
const CloudApiInfoModal = lazy(() => import('../../WhatsAppCloudApi/components/CloudApiInfoModal'));

// ===============================================
// CONSTANTS
// ===============================================

const CHANNELS = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    desc: 'Conexao via QR Code',
    icon: IconBrandWhatsapp,
    color: '#25D366',
    gradient: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
    enabled: true,
  },
  {
    id: 'whatsapp_cloud',
    name: 'WhatsApp API Oficial',
    desc: 'Conexao oficial Meta Business',
    icon: IconBrandMeta,
    color: '#1877F2',
    gradient: 'linear-gradient(135deg, #1877F2 0%, #0d5bc4 100%)',
    enabled: true,
    isNew: true,
  },
  {
    id: 'instagram',
    name: 'Instagram',
    desc: 'Direct Messages',
    icon: IconBrandInstagram,
    color: '#E4405F',
    gradient: 'linear-gradient(135deg, #E4405F 0%, #C13584 100%)',
    enabled: false,
  },
  {
    id: 'site',
    name: 'Site',
    desc: 'Widget no site',
    icon: IconWorld,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    enabled: false,
  },
];

const STEPS = {
  CHANNEL_SELECT: 'CHANNEL_SELECT',
  WA_CHECKING: 'WA_CHECKING',
  WA_SESSIONS: 'WA_SESSIONS',
  WA_NEW: 'WA_NEW',
  WA_GENERATING: 'WA_GENERATING',
  WA_CONNECTING: 'WA_CONNECTING',
  CLOUD_API_CONNECT: 'CLOUD_API_CONNECT',
  CLOUD_API_CONNECTED: 'CLOUD_API_CONNECTED',
  PUBLISHING: 'PUBLISHING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};

// Phone mask format function (same as WhatsappAgents.js)
const formatPhoneMask = (value) => {
  const clean = value.replace(/\D/g, '');
  if (clean.length === 0) return '';
  if (clean.length <= 2) return `(${clean}`;
  if (clean.length <= 6) return clean.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  if (clean.length <= 10) return clean.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return clean.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

// ===============================================
// HELPERS
// ===============================================

const getStatusInfo = (status) => {
  switch (status) {
    case 'WORKING':
      return { label: 'Conectado', className: 'pub-badge--success' };
    case 'FAILED':
      return { label: 'Falha na conexão', className: 'pub-badge--danger' };
    case 'SCAN_QR_CODE':
      return { label: 'Aguardando QR Code', className: 'pub-badge--warning' };
    case 'STOPPED':
      return { label: 'Parado', className: 'pub-badge--dark' };
    case 'STARTING':
      return { label: 'Iniciando', className: 'pub-badge--info' };
    default:
      return { label: status || 'Desconhecido', className: 'pub-badge--dark' };
  }
};

const extractPhoneFromMe = (me) => {
  if (me?.id) {
    const parts = me.id.split('@');
    if (parts[0] && /^\d+$/.test(parts[0])) {
      return parts[0];
    }
  }
  return null;
};

// ===============================================
// SUB-COMPONENT: ChannelCard
// ===============================================

const ChannelCard = React.memo(({ channel, isSelected, isConnected, onSelect }) => {
  const Icon = channel.icon;
  const classes = [
    'pub-channel',
    isSelected && 'pub-channel--selected',
    !channel.enabled && 'pub-channel--disabled',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      onClick={() => channel.enabled && onSelect(channel.id)}
      role="button"
      tabIndex={channel.enabled ? 0 : -1}
    >
      {!channel.enabled && <span className="pub-channel__badge">Em breve</span>}
      {isConnected && <span className="pub-channel__badge pub-channel__badge--connected">Conectado</span>}
      <div
        className="pub-channel__icon"
        style={{ background: channel.gradient }}
      >
        <Icon size={24} />
      </div>
      <p className="pub-channel__name">{channel.name}</p>
      <p className="pub-channel__desc">{channel.desc}</p>
    </div>
  );
});

// ===============================================
// MAIN COMPONENT
// ===============================================

const PublishChannelModal = ({
  show,
  onHide,
  agentId,
  agentName,
  wabottyAccount,
  onPublishSuccess,
  mode = 'publish', // 'publish' | 'channels'
  nodes = [],
  // publishAgent e isProPlan ficavam aqui — agora vêm via usePublishValidation()
  // internamente. Props mantidas no caller continuam funcionando se passadas
  // (ignoradas), mas o caller pode parar de passar.
}) => {
  // Context
  const { setNotify } = useContext(NotificationsContext);
  const { bottyData, fetchWaAccount, user } = useContext(UserContext);

  // Acesso ao card "WhatsApp API Oficial" liberado apenas para contas piloto.
  // Fonte única: src/screens/AgentsFlow/constants/featureFlags.js
  const canSeeCloudApi = isCloudApiEnabledForAccount(user?.account_id);
  const visibleChannels = useMemo(
    () => CHANNELS.filter((ch) => ch.id !== 'whatsapp_cloud' || canSeeCloudApi),
    [canSeeCloudApi]
  );

  // State
  const [step, setStep] = useState(STEPS.CHANNEL_SELECT);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isGeneratingSession, setIsGeneratingSession] = useState(false);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [cloudApiStatus, setCloudApiStatus] = useState(null);
  const [showCloudApiInfo, setShowCloudApiInfo] = useState(false);
  const connectOrReconnectRef = useRef('connect');

  // Refs for cleanup of polling intervals/timeouts
  const sessionIntervalRef = useRef(null);
  const sessionTimeoutRef = useRef(null);

  // Cleanup polling on unmount or modal close
  const clearSessionPolling = useCallback(() => {
    if (sessionIntervalRef.current) {
      clearInterval(sessionIntervalRef.current);
      sessionIntervalRef.current = null;
    }
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setStep(STEPS.CHANNEL_SELECT);
      setSelectedChannel(null);
      setSessions([]);
      setSelectedSession(null);
      setPhoneNumber('');
      setErrorMessage('');
      setIsGeneratingSession(false);
      setIsDeletingSession(false);
      setCloudApiStatus(null);
    } else {
      clearSessionPolling();
      Swal.close();
    }
  }, [show, clearSessionPolling]);

  // Fetch Cloud API status when modal opens (to show "Conectado" badge on the card)
  useEffect(() => {
    if (!show || !agentId) return;
    let cancelled = false;
    (async () => {
      try {
        const { default: WhatsAppCloudApiService } = await import('../../WhatsAppCloudApi/services/WhatsAppCloudApiService');
        const resp = await WhatsAppCloudApiService.getStatus(agentId);
        if (!cancelled) setCloudApiStatus(resp.data);
      } catch {
        if (!cancelled) setCloudApiStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [show, agentId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearSessionPolling();
  }, [clearSessionPolling]);

  // ===============================================
  // CALLBACKS
  // ===============================================

  const handleChannelSelect = useCallback((id) => {
    setSelectedChannel(id);
  }, []);

  const handleWhatsAppProceed = useCallback(async () => {
    // Always fetch sessions so the user can manage (pause/activate/delete)
    setStep(STEPS.WA_CHECKING);
    try {
      const [accountsData, quotaData] = await Promise.all([
        GetAccounts(),
        GetWhatsappFeatureUsage(),
      ]);

      const rawSessions = Array.isArray(accountsData) ? accountsData : (accountsData?.sessions || []);
      // Filtrar: este wizard é exclusivo WAHA (QR Code). Contas Cloud API
      // aparecem no card "WhatsApp API Oficial" via fluxo próprio
      // (CloudApiConnectModal + CLOUD_API_CONNECTED step).
      const sessionsList = rawSessions.filter((s) => s?.provider !== 'cloud_api');

      if (sessionsList.length > 0) {
        setSessions(sessionsList);

        // Pre-select session that matches the agent's wabotty_account or agent_flow_id
        if (wabottyAccount || agentId) {
          const matched = sessionsList.find((s) => {
            const mePhone = extractPhoneFromMe(s.me);
            return (
              (wabottyAccount && mePhone && wabottyAccount.includes(mePhone)) ||
              (wabottyAccount && s.phone && wabottyAccount.includes(s.phone)) ||
              (s.agent_flow_id && String(s.agent_flow_id) === String(agentId))
            );
          });
          if (matched) {
            setSelectedSession(matched);
          }
        }

        setStep(STEPS.WA_SESSIONS);
      } else if (quotaData?.success && quotaData.usage_remaining > 0) {
        setStep(STEPS.WA_NEW);
      } else {
        setErrorMessage(
          quotaData?.usage_remaining === 0
            ? 'Limite de conexões WhatsApp atingido. Faça upgrade do seu plano para conectar mais números.'
            : 'Não foi possível verificar o limite de conexões. Tente novamente.'
        );
        setStep(STEPS.ERROR);
      }
    } catch (error) {
      console.error('Erro ao verificar WhatsApp:', error);
      setErrorMessage('Erro ao verificar conexões WhatsApp. Tente novamente.');
      setStep(STEPS.ERROR);
    }
  }, [wabottyAccount, agentId]);

  // Validação + publish vivem em usePublishValidation (compartilhado).
  const { validateAndPublish } = usePublishValidation();

  const handleDoPublish = useCallback(async () => {
    setStep(STEPS.PUBLISHING);
    // Não temos `edges` aqui (PublishChannelModal recebe só `nodes` via prop).
    // Passamos undefined para nodes E edges → hook busca graph SALVO via API
    // e valida com graph completo. Trade-off: edits não salvos não entram na
    // validação local, mas backend revalida antes de publicar de fato.
    const result = await validateAndPublish(agentId, undefined, undefined, { silent: true });

    if (result.success) {
      setStep(STEPS.SUCCESS);
      setNotify({
        title: 'Agente Publicado!',
        message: 'Seu agente foi publicado com sucesso e está pronto para uso.',
        type: 'success',
        visible: true,
      });
      if (onPublishSuccess) onPublishSuccess();
      return;
    }

    // Pré-validação falhou (PRO, LLM missing, graph): hook já mostrou Swal —
    // só voltamos para a tela de seleção de canal.
    if (result.reason !== 'api_error') {
      setStep(STEPS.CHANNEL_SELECT);
      return;
    }

    // Erro da API: hook já mostrou Swal contextual; voltamos para channel select
    // para preservar o que o usuário já tinha selecionado.
    setStep(STEPS.CHANNEL_SELECT);
  }, [agentId, validateAndPublish, onPublishSuccess, setNotify]);

  const handleCloudApiProceed = useCallback(async () => {
    try {
      const { default: WhatsAppCloudApiService } = await import('../../WhatsAppCloudApi/services/WhatsAppCloudApiService');
      const resp = await WhatsAppCloudApiService.getStatus(agentId);
      if (resp.data?.connected) {
        setCloudApiStatus(resp.data);
        setStep(STEPS.CLOUD_API_CONNECTED);
        return;
      }
    } catch {
      // Not connected, proceed to connect
    }
    setStep(STEPS.CLOUD_API_CONNECT);
  }, [agentId]);

  const handleCloudApiDisconnect = useCallback(async () => {
    const result = await Swal.fire({
      title: 'Desconectar WhatsApp Cloud API?',
      text: 'O agente deixará de receber e enviar mensagens pelo número conectado.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Desconectar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      const { default: WhatsAppCloudApiService } = await import('../../WhatsAppCloudApi/services/WhatsAppCloudApiService');
      await WhatsAppCloudApiService.disconnect(agentId);
      setCloudApiStatus(null);
      setStep(STEPS.CLOUD_API_CONNECT);
      Swal.fire({
        icon: 'success',
        title: 'Desconectado',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao desconectar',
        text: err.response?.data?.error || 'Tente novamente.',
      });
    }
  }, [agentId]);

  const handleCloudApiReconnect = useCallback(() => {
    setStep(STEPS.CLOUD_API_CONNECT);
  }, []);

  const handleProceed = useCallback(() => {
    if (selectedChannel === 'whatsapp') {
      handleWhatsAppProceed();
    } else if (selectedChannel === 'whatsapp_cloud') {
      handleCloudApiProceed();
    }
  }, [selectedChannel, handleWhatsAppProceed, handleCloudApiProceed]);

  const handleCloudApiConnected = useCallback(async (result) => {
    // Cloud API Manual/Embedded retornou sucesso.
    // Busca status atualizado para enriquecer a tela de confirmação.
    try {
      const { default: WhatsAppCloudApiService } = await import('../../WhatsAppCloudApi/services/WhatsAppCloudApiService');
      const resp = await WhatsAppCloudApiService.getStatus(agentId);
      setCloudApiStatus(resp.data);
    } catch {
      // fallback: usa o que o wizard retornou
      setCloudApiStatus({
        connected: true,
        display_phone_number: result?.phone_number || '',
        business_name: result?.business_name || '',
        quality_rating: result?.quality_rating || '',
      });
    }
    // Mostra a tela de conexão ativa (channels/publish) antes de publicar.
    setStep(STEPS.CLOUD_API_CONNECTED);
  }, [agentId]);

  const handleLinkSession = useCallback(async () => {
    if (!selectedSession) return;
    setStep(STEPS.PUBLISHING);
    try {
      // Call update_session directly to send agent_flow_id (not agent_id)
      // The shared updateSession() from WaBottyServices sends agent_id,
      // which is the wrong field for AgentsFlow agents.
      const phone = selectedSession.phone || selectedSession.name;
      const payload = {
        phone,
        type: 'link',
        agent_flow_id: agentId,
      };
      const response = await api.put(`${WABOTTY_URL}update_session/`, payload);

      if (response.status === 200) {
        setNotify({
          title: 'WhatsApp Vinculado!',
          message: 'Sessão vinculada com sucesso ao agente.',
          type: 'success',
          visible: true,
        });
        try { await fetchWaAccount(); } catch { /* non-blocking */ }
        await handleDoPublish();
      } else {
        setErrorMessage('Erro ao vincular sessão WhatsApp.');
        setStep(STEPS.ERROR);
      }
    } catch (error) {
      console.error('Erro ao vincular sessão:', error);
      const errMsg = error?.response?.data?.error || 'Erro ao vincular sessão WhatsApp. Tente novamente.';
      setErrorMessage(errMsg);
      setStep(STEPS.ERROR);
    }
  }, [selectedSession, agentId, handleDoPublish, setNotify, fetchWaAccount]);

  // Link only (channels mode — no publish after linking)
  const handleLinkOnly = useCallback(async () => {
    if (!selectedSession) return;
    setStep(STEPS.PUBLISHING);
    try {
      const phone = selectedSession.phone || selectedSession.name;
      const payload = {
        phone,
        type: 'link',
        agent_flow_id: agentId,
      };
      const response = await api.put(`${WABOTTY_URL}update_session/`, payload);

      if (response.status === 200) {
        setNotify({
          title: 'WhatsApp Vinculado!',
          message: 'Sessão vinculada com sucesso ao agente.',
          type: 'success',
          visible: true,
        });
        try { await fetchWaAccount(); } catch { /* non-blocking */ }
        setStep(STEPS.SUCCESS);
      } else {
        setErrorMessage('Erro ao vincular sessão WhatsApp.');
        setStep(STEPS.ERROR);
      }
    } catch (error) {
      console.error('Erro ao vincular sessão:', error);
      const errMsg = error?.response?.data?.error || 'Erro ao vincular sessão WhatsApp. Tente novamente.';
      setErrorMessage(errMsg);
      setStep(STEPS.ERROR);
    }
  }, [selectedSession, agentId, setNotify, fetchWaAccount]);

  // -----------------------------------------------
  // handleGenerateSession — mirrors WhatsappAgents.js
  // Calls ConnectWhatsappAccount, then polls CheckAccountStatus
  // until WORKING / SCAN_QR_CODE before showing WhatsappConnect.
  // -----------------------------------------------
  const handleGenerateSession = useCallback(async () => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (!cleanPhone || (cleanPhone.length !== 10 && cleanPhone.length !== 11)) {
      Swal.fire({
        icon: 'warning',
        title: 'Número inválido',
        text: 'Por favor, insira um número de telefone válido (10 ou 11 dígitos).',
        confirmButtonText: 'Entendi',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }

    setIsGeneratingSession(true);
    setStep(STEPS.WA_GENERATING);

    try {
      const connectParams = {
        phone: cleanPhone,
        agent_flow_id: agentId || null,
      };

      const response = await ConnectWhatsappAccount(connectParams);

      if (response?.status === 'STARTING') {
        // Show Swal loading while polling for session to be ready
        Swal.fire({
          title: 'Aguardando sessão inicializar...',
          html: `
            <div style="text-align: center; margin: 20px 0;">
              <p>Aguarde enquanto a sessão do WhatsApp é iniciada.</p>
              <p><strong>Isso pode levar até 2 minutos.</strong></p>
              <div style="background: #e3f2fd; padding: 12px; border-radius: 8px; margin-top: 16px;">
                <small style="color: #1976d2;">⏱️ Verificando status...</small>
              </div>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => { Swal.showLoading(); },
        });

        // Poll every 3s
        // IMPORTANT: pass null as agentId to avoid CheckAccountStatus calling
        // updateAgent from AgentsServices.js (wrong endpoint /agents/).
        // We handle the wabotty_account update ourselves via AgentsFlowService
        // (correct endpoint /agents_flow/agents/) in handleConnectionSuccess.
        sessionIntervalRef.current = setInterval(async () => {
          try {
            const result = await CheckAccountStatus(cleanPhone, null);
            const status = result?.status || result?.data?.status;

            if (status === 'WORKING' || status === 'SCAN_QR_CODE') {
              clearSessionPolling();
              Swal.close();
              setIsGeneratingSession(false);
              connectOrReconnectRef.current = 'connect';
              setStep(STEPS.WA_CONNECTING);
            }
          } catch (err) {
            console.error('Erro ao verificar status da sessão:', err);
          }
        }, 3000);

        // Timeout 2 minutes
        sessionTimeoutRef.current = setTimeout(() => {
          clearSessionPolling();
          setIsGeneratingSession(false);
          Swal.close();
          setErrorMessage(
            'A sessão do WhatsApp não conseguiu inicializar em 2 minutos. Possíveis causas: problemas de conectividade, sobrecarga no servidor ou número já em uso. Tente novamente em alguns minutos.'
          );
          setStep(STEPS.ERROR);
        }, 120000);
      } else {
        // Unexpected status from ConnectWhatsappAccount
        setIsGeneratingSession(false);
        setErrorMessage('Não foi possível iniciar a sessão do WhatsApp. Tente novamente.');
        setStep(STEPS.ERROR);
      }
    } catch (error) {
      console.error('Erro ao iniciar sessão WhatsApp:', error);
      setIsGeneratingSession(false);

      const errorData = error?.response?.data || error?.data;

      if (errorData?.requires_payment) {
        // Quota exceeded
        setErrorMessage(
          `Limite de conexões excedido (${errorData.current_usage}/${errorData.total_limit}). ${errorData.error || 'Faça upgrade do seu plano para conectar mais números.'}`
        );
        setStep(STEPS.ERROR);
      } else if (error?.status === 422 || error?.response?.status === 422) {
        // 422 means session already exists — go directly to WhatsappConnect
        connectOrReconnectRef.current = 'connect';
        setStep(STEPS.WA_CONNECTING);
      } else if (errorData?.error) {
        setErrorMessage(errorData.error);
        setStep(STEPS.ERROR);
      } else {
        setErrorMessage('Erro ao conectar ao WhatsApp. Tente novamente.');
        setStep(STEPS.ERROR);
      }
    }
  }, [phoneNumber, agentId, clearSessionPolling]);

  const handleConnectionSuccess = useCallback(async (data) => {
    // Format phone the same way as WhatsappAgents.js
    const formattedPhone = data?.phone || formatPhoneForAPI(phoneNumber).replace('+', '');

    // Update wabotty_account via AgentsFlowService (correct endpoint /agents_flow/agents/)
    // We pass agentId=null to WhatsappConnect to avoid CheckAccountStatus calling
    // the wrong endpoint (/agents/), so we handle the update ourselves here.
    try {
      await AgentsFlowService.updateAgent(agentId, {
        wabotty_account: formattedPhone,
      });
    } catch (err) {
      console.error('Erro ao atualizar wabotty_account no agente:', err);
      // Non-blocking: continue to publish even if this update fails
      // (the backend publish endpoint may handle it)
    }

    // Re-hidrata bottyData no UserContext para a Central de Atendimento
    // reconhecer a nova conta sem exigir reload.
    try { await fetchWaAccount(); } catch { /* non-blocking */ }

    handleDoPublish();
  }, [phoneNumber, agentId, handleDoPublish, fetchWaAccount]);

  const handleConnectionFailure = useCallback(() => {
    setErrorMessage('Não foi possível conectar ao WhatsApp. Tente novamente.');
    setStep(STEPS.ERROR);
  }, []);

  const handleDeleteSession = useCallback((session, e) => {
    e.stopPropagation(); // Prevent selecting the session
    Swal.fire({
      title: 'Você tem certeza?',
      text: 'Você está prestes a excluir esta conexão do WhatsApp!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Excluir Conexão',
    }).then((result) => {
      if (result.isConfirmed) {
        setIsDeletingSession(true);
        const deletePayload = {
          session_name: session.name,
        };

        DeleteAccount(deletePayload)
          .then(() => {
            setNotify({
              title: 'Sucesso!',
              message: 'Conexão com WhatsApp excluída com sucesso!',
              type: 'success',
              visible: true,
            });
            // Remove from local list and re-check
            const updatedSessions = sessions.filter((s) => s !== session);
            setSessions(updatedSessions);
            if (selectedSession === session) {
              setSelectedSession(null);
            }
            // If no sessions left, go to WA_NEW
            if (updatedSessions.length === 0) {
              handleWhatsAppProceed();
            }
          })
          .catch(() => {
            setNotify({
              title: 'Oops!',
              message: 'Ocorreu um erro ao excluir a conexão. Tente novamente.',
              type: 'error',
              visible: true,
            });
          })
          .finally(() => {
            setIsDeletingSession(false);
          });
      }
    });
  }, [sessions, selectedSession, setNotify, handleWhatsAppProceed]);

  const handlePauseOrActivate = useCallback((session, action, e) => {
    e.stopPropagation();
    const isPausing = action === 'pause';
    Swal.fire({
      title: 'Você tem certeza?',
      html: isPausing
        ? 'Você está prestes a <strong>pausar</strong> o funcionamento da IA nesta conta!'
        : 'Você tem certeza que deseja <strong>ativar</strong> a IA nesta conta?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: isPausing ? '#515151' : 'var(--success, #06b776)',
      cancelButtonColor: '#d33',
      confirmButtonText: isPausing ? 'Pausar IA/Sessão!' : 'Ativar IA/Sessão',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        const phone = extractPhoneFromMe(session.me) || session.phone || session.name;
        PauseOrActiveAccount({ status: isPausing ? 0 : 1, phone })
          .then(() => {
            setNotify({
              title: 'Sucesso!',
              message: isPausing ? 'IA pausada com sucesso!' : 'IA ativada com sucesso!',
              type: 'success',
              visible: true,
            });
            // Update session status locally
            setSessions((prev) =>
              prev.map((s) =>
                s === session ? { ...s, status: isPausing ? 'STOPPED' : 'WORKING' } : s
              )
            );
            // Deselect if paused
            if (isPausing && selectedSession === session) {
              setSelectedSession(null);
            }
          })
          .catch(() => {
            setNotify({
              title: 'Oops!',
              message: `Ocorreu um erro ao ${isPausing ? 'pausar' : 'ativar'} a sessão. Tente novamente.`,
              type: 'error',
              visible: true,
            });
          });
      }
    });
  }, [selectedSession, setNotify]);

  const handleBack = useCallback(() => {
    clearSessionPolling();
    setIsGeneratingSession(false);
    setStep(STEPS.CHANNEL_SELECT);
    setSelectedSession(null);
    setPhoneNumber('');
  }, [clearSessionPolling]);

  const handleClose = useCallback(() => {
    if (step === STEPS.PUBLISHING) {
      Swal.fire({
        title: 'Publicação em andamento',
        text: 'Aguarde a conclusão da publicação.',
        icon: 'info',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }
    clearSessionPolling();
    onHide();
  }, [step, onHide, clearSessionPolling]);

  const handleRetry = useCallback(() => {
    setErrorMessage('');
    setStep(STEPS.CHANNEL_SELECT);
  }, []);

  // ===============================================
  // DERIVED
  // ===============================================

  // Validate phone: 10 or 11 digits
  const cleanPhone = phoneNumber.replace(/\D/g, '');
  const isPhoneValid = cleanPhone.length === 10 || cleanPhone.length === 11;

  // ===============================================
  // RENDER HELPERS
  // ===============================================

  const isChannelsMode = mode === 'channels';

  const renderHeader = useMemo(() => (
    <div className="pub-header">
      <button className="pub-header__close" onClick={handleClose}>
        <IconX size={16} />
      </button>
      <div className="pub-header__icon" style={isChannelsMode ? { background: 'linear-gradient(135deg, #06b776 0%, #059669 100%)' } : undefined}>
        {isChannelsMode ? <IconPlugConnected size={28} /> : <IconRocket size={28} />}
      </div>
      <h4 className="pub-header__title">{isChannelsMode ? 'Conectar Canais' : 'Publicar Agente'}</h4>
      <p className="pub-header__subtitle">
        {isChannelsMode
          ? (agentName ? `Gerencie os canais de "${agentName}"` : 'Gerencie os canais do seu agente')
          : (agentName ? `Escolha onde publicar "${agentName}"` : 'Escolha onde publicar seu agente')
        }
      </p>
    </div>
  ), [agentName, handleClose, isChannelsMode]);

  const renderChannelSelect = () => (
    <>
      <div className="pub-content">
        <div className="pub-channels__grid">
          {visibleChannels.map((ch) => (
            <ChannelCard
              key={ch.id}
              channel={ch}
              isSelected={selectedChannel === ch.id}
              isConnected={
                (ch.id === 'whatsapp' && Array.isArray(bottyData) && bottyData.some(b => b?.status === 'WORKING' && b?.provider !== 'cloud_api')) ||
                (ch.id === 'whatsapp_cloud' && cloudApiStatus?.connected === true)
              }
              onSelect={handleChannelSelect}
            />
          ))}
        </div>
        {selectedChannel === 'whatsapp_cloud' && (
          <div
            style={{
              marginTop: 16,
              padding: '12px 14px',
              borderRadius: 10,
              border: '1px solid #fcd34d',
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <IconAlertTriangle size={18} style={{ color: '#b45309', flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>
              <strong style={{ display: 'block', marginBottom: 2 }}>
                Recurso em fase final de desenvolvimento
              </strong>
              A integração com a API Oficial do WhatsApp (Meta Business) está nos
              ajustes finais e aguardando a liberação da meta.
            </div>
          </div>
        )}
      </div>
      <div className="pub-footer">
        <button className="pub-btn--secondary" onClick={handleClose}>
          Cancelar
        </button>
        <button
          className="pub-btn--primary"
          disabled={!selectedChannel}
          onClick={handleProceed}
        >
          Continuar
        </button>
      </div>
    </>
  );

  const renderWaChecking = () => (
    <div className="pub-content">
      <div className="pub-loading">
        <Spinner animation="border" variant="primary" />
        <span className="pub-loading__text">Verificando conexões WhatsApp...</span>
      </div>
    </div>
  );

  const renderWaSessions = () => (
    <>
      <div className="pub-content">
        <p className="pub-section__label">Selecione uma sessão WhatsApp</p>
        <p className="pub-section__desc">
          Escolha um número já conectado para vincular ao agente.
        </p>
        <div className="pub-sessions__list">
          {sessions.map((session, idx) => {
            const mePhone = extractPhoneFromMe(session.me);
            const displayPhone = mePhone || session.phone || null;
            const sessionId = session.name || `Sessão ${idx + 1}`;
            const statusInfo = getStatusInfo(session.status);
            const isSelected = selectedSession === session;
            const canSelect = session.status === 'WORKING';
            const canPause = session.status === 'WORKING';
            const canActivate = session.status === 'STOPPED';

            return (
              <div
                key={sessionId}
                className={`pub-session ${isSelected ? 'pub-session--selected' : ''} ${!canSelect ? 'pub-session--unavailable' : ''}`}
                onClick={() => canSelect && setSelectedSession(session)}
              >
                <div className="pub-session__icon">
                  <IconBrandWhatsapp size={16} />
                </div>
                <div className="pub-session__info">
                  {displayPhone && (
                    <p className="pub-session__phone">{displayPhone}</p>
                  )}
                  <p className="pub-session__id">{sessionId}</p>
                </div>
                <div className="pub-session__actions">
                  <span className={`pub-badge ${statusInfo.className}`}>
                    {statusInfo.label}
                  </span>
                  {canPause && (
                    <button
                      className="pub-session__action-btn pub-session__action-btn--pause"
                      onClick={(e) => handlePauseOrActivate(session, 'pause', e)}
                      title="Pausar IA"
                    >
                      <IconPlayerPause size={14} />
                    </button>
                  )}
                  {canActivate && (
                    <button
                      className="pub-session__action-btn pub-session__action-btn--activate"
                      onClick={(e) => handlePauseOrActivate(session, 'activate', e)}
                      title="Ativar IA"
                    >
                      <IconPlayerPlay size={14} />
                    </button>
                  )}
                  <button
                    className="pub-session__action-btn pub-session__action-btn--delete"
                    onClick={(e) => handleDeleteSession(session, e)}
                    disabled={isDeletingSession}
                    title="Excluir conexão"
                  >
                    {isDeletingSession ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <IconTrash size={14} />
                    )}
                  </button>
                  {isSelected && (
                    <IconCheck size={18} className="pub-session__check" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="pub-footer">
        <button className="pub-btn--secondary" onClick={handleBack}>
          Voltar
        </button>
        <button
          className="pub-btn--primary"
          disabled={!selectedSession}
          onClick={isChannelsMode ? handleLinkOnly : handleLinkSession}
        >
          {isChannelsMode ? <IconPlugConnected size={16} /> : <IconRocket size={16} />}
          {isChannelsMode ? 'Vincular' : 'Vincular e Publicar'}
        </button>
      </div>
    </>
  );

  const renderWaNew = () => (
    <>
      <div className="pub-content">
        <p className="pub-section__label">Conectar novo número</p>
        <p className="pub-section__desc">
          Digite o número do WhatsApp que deseja conectar ao agente.
        </p>
        <div>
          <label className="pub-phone__label">
            <IconPhone size={14} style={{ marginRight: 4 }} />
            Número do WhatsApp
          </label>
          <NumberFormat
            className="pub-phone__input"
            format={formatPhoneMask}
            mask="_"
            placeholder="(00) 00000-0000"
            value={phoneNumber}
            onValueChange={(values) => {
              setPhoneNumber(values.formattedValue);
            }}
          />
          <p className="pub-phone__hint">
            Aceita telefones fixos (10 dígitos) e celulares (11 dígitos)
          </p>
        </div>
      </div>
      <div className="pub-footer">
        <button className="pub-btn--secondary" onClick={handleBack}>
          Voltar
        </button>
        <button
          className="pub-btn--primary"
          disabled={isGeneratingSession || !isPhoneValid}
          onClick={handleGenerateSession}
        >
          {isGeneratingSession ? (
            <>
              <Spinner animation="border" size="sm" style={{ marginRight: 6 }} />
              Iniciando sessão...
            </>
          ) : (
            'Gerar QrCode de conexão'
          )}
        </button>
      </div>
    </>
  );

  const renderWaGenerating = () => (
    <div className="pub-content">
      <div className="pub-loading">
        <Spinner animation="border" variant="primary" />
        <span className="pub-loading__text">Iniciando sessão WhatsApp...</span>
      </div>
    </div>
  );

  const renderWaConnecting = () => (
    <div className="pub-content">
      <div className="pub-whatsapp-connect">
        <Suspense
          fallback={
            <div className="pub-loading">
              <Spinner animation="border" variant="primary" />
              <span className="pub-loading__text">Carregando...</span>
            </div>
          }
        >
          {/* agentId=null to prevent WhatsappConnect → CheckAccountStatus
              from calling updateAgent via /agents/ (wrong endpoint).
              We update wabotty_account via AgentsFlowService in handleConnectionSuccess. */}
          <WhatsappConnect
            phoneNumber={phoneNumber.replace(/\D/g, '')}
            agentId={null}
            connectOrReconnect={connectOrReconnectRef}
            onConnectionSuccess={handleConnectionSuccess}
            onConnectionFailure={handleConnectionFailure}
          />
        </Suspense>
      </div>
    </div>
  );

  const renderCloudApiConnected = () => {
    const s = cloudApiStatus || {};
    return (
      <>
        <div className="pub-content">
          <p className="pub-section__label">WhatsApp API Oficial</p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <p className="pub-section__desc" style={{ margin: 0 }}>
              Este agente está vinculado ao número abaixo.
            </p>
            <button
              type="button"
              onClick={() => setShowCloudApiInfo(true)}
              aria-label="Ver informações sobre qualidade e tier"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid #1877F2',
                background: 'rgba(24, 119, 242, 0.08)',
                color: '#1877F2',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <IconInfoCircle size={14} />
              Informações
            </button>
          </div>
          <div className="pub-sessions__list">
            <div className="pub-session pub-session--selected">
              <div
                className="pub-session__icon"
                style={{ background: 'linear-gradient(135deg, #e7f3ff 0%, #d0e7ff 100%)', color: '#1877F2' }}
              >
                <IconBrandMeta size={16} />
              </div>
              <div className="pub-session__info">
                <p className="pub-session__phone">
                  {s.display_phone_number || '—'}
                </p>
                <p className="pub-session__id">
                  {s.business_name || 'WhatsApp Business Account'}
                  {s.quality_rating ? ` · Qualidade: ${s.quality_rating}` : ''}
                  {s.messaging_limit ? ` · Tier: ${s.messaging_limit}` : ''}
                </p>
              </div>
              <div className="pub-session__actions">
                <span className="pub-badge pub-badge--success">Conectado</span>
                <button
                  className="pub-session__action-btn pub-session__action-btn--delete"
                  onClick={handleCloudApiDisconnect}
                  title="Desconectar"
                >
                  <IconTrash size={14} />
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
            <button
              className="pub-btn--secondary"
              onClick={handleCloudApiReconnect}
              style={{ fontSize: 12 }}
            >
              Reconectar com outras credenciais
            </button>
          </div>
        </div>
        <div className="pub-footer">
          <button className="pub-btn--secondary" onClick={handleBack}>
            Voltar
          </button>
          <button
            className="pub-btn--primary"
            onClick={isChannelsMode ? handleClose : handleDoPublish}
          >
            {isChannelsMode ? <IconPlugConnected size={16} /> : <IconRocket size={16} />}
            {isChannelsMode ? 'Pronto' : 'Publicar agente'}
          </button>
        </div>
      </>
    );
  };

  const renderPublishing = () => (
    <div className="pub-content">
      <div className="pub-loading">
        <Spinner animation="border" variant="primary" />
        <span className="pub-loading__text">Publicando agente...</span>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <>
      <div className="pub-content">
        <div className="pub-result pub-result--success">
          <div className="pub-result__icon">
            <IconCheck size={28} />
          </div>
          <h5 className="pub-result__title">
            {isChannelsMode ? 'Canal vinculado!' : 'Agente publicado!'}
          </h5>
          <p className="pub-result__desc">
            {isChannelsMode
              ? 'O canal foi vinculado com sucesso ao agente.'
              : 'Seu agente está pronto para receber mensagens.'
            }
          </p>
        </div>
      </div>
      <div className="pub-footer">
        <button className="pub-btn--primary" onClick={onHide}>
          Fechar
        </button>
      </div>
    </>
  );

  const renderError = () => (
    <>
      <div className="pub-content">
        <div className="pub-result pub-result--error">
          <div className="pub-result__icon">
            <IconAlertTriangle size={28} />
          </div>
          <h5 className="pub-result__title">Erro na publicação</h5>
          <p className="pub-result__desc">{errorMessage}</p>
        </div>
      </div>
      <div className="pub-footer">
        <button className="pub-btn--secondary" onClick={onHide}>
          Fechar
        </button>
        <button className="pub-btn--primary" onClick={handleRetry}>
          Tentar Novamente
        </button>
      </div>
    </>
  );

  // ===============================================
  // STEP ROUTER
  // ===============================================

  const renderStepContent = () => {
    switch (step) {
      case STEPS.CHANNEL_SELECT:
        return renderChannelSelect();
      case STEPS.WA_CHECKING:
        return renderWaChecking();
      case STEPS.WA_SESSIONS:
        return renderWaSessions();
      case STEPS.WA_NEW:
        return renderWaNew();
      case STEPS.WA_GENERATING:
        return renderWaGenerating();
      case STEPS.WA_CONNECTING:
        return renderWaConnecting();
      case STEPS.CLOUD_API_CONNECT:
        return (
          <Suspense fallback={<div className="pub-content" style={{ textAlign: 'center', padding: '2rem' }}><Spinner animation="border" /></div>}>
            <CloudApiConnectModal
              show={true}
              onHide={() => setStep(STEPS.CHANNEL_SELECT)}
              agentId={agentId}
              onConnected={handleCloudApiConnected}
            />
          </Suspense>
        );
      case STEPS.CLOUD_API_CONNECTED:
        return renderCloudApiConnected();
      case STEPS.PUBLISHING:
        return renderPublishing();
      case STEPS.SUCCESS:
        return renderSuccess();
      case STEPS.ERROR:
        return renderError();
      default:
        return null;
    }
  };

  // Show header only for CHANNEL_SELECT
  const showHeader = step === STEPS.CHANNEL_SELECT;

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        className="pub-modal"
        backdrop={step === STEPS.PUBLISHING || step === STEPS.WA_GENERATING ? 'static' : true}
        keyboard={step !== STEPS.PUBLISHING && step !== STEPS.WA_GENERATING}
      >
        {showHeader && renderHeader}
        {renderStepContent()}
      </Modal>
      {showCloudApiInfo && (
        <Suspense fallback={null}>
          <CloudApiInfoModal
            show={showCloudApiInfo}
            onHide={() => setShowCloudApiInfo(false)}
          />
        </Suspense>
      )}
    </>
  );
};

export default PublishChannelModal;
